import { z } from 'zod';

/**
 * 인스턴스 생존 (26회차).
 *
 * 25회차까지 "지금 떠 있는 인스턴스"는 실시간 링버퍼 그 자체였다. 버퍼는 30분치를 들고 있고
 * 사라진 인스턴스를 걷어내는 시간 제한이 없어서, 죽은 프로세스가 30분 동안 살아 있는 것처럼
 * 목록에 남았다. 재시작도 알 수 없었다 — 에이전트가 `processStartedAt`을 보내는데 서버가
 * 아무 데도 쓰지 않았다.
 *
 * 여기에는 두 종류가 섞여 있다.
 *   - **실시간 인스턴스**: 우리 에이전트가 1초 스냅샷을 보낸다. `realtime: true`
 *   - **트레이스만 인스턴스**: 표준 OTel로 붙어 트레이스만 보낸다(OTel Java agent 등).
 *     스냅샷이 없으므로 **끝난 요청이 도착한 시각**으로 살아 있음을 판정한다. `realtime: false`
 *
 * 둘은 신호가 오는 간격이 크게 다르다. 스냅샷은 1초마다 오지만 요청은 뜸할 수 있어
 * 조용해졌다고 볼 시간(`stale`)과 목록에서 뺄 시간도 따로 둔다(`APM_RT_TRACE_STALE_MS`·
 * `APM_RT_TRACE_GONE_MS`).
 */

const epochMs = z.number().int().nonnegative();

export const InstancePresence = z.object({
  service: z.string().min(1),
  /** OTel `service.instance.id`. 안 보내는 계측이면 없다 */
  instance: z.string().optional(),
  /** 마지막 신호(스냅샷 또는 끝난 요청) 시각. epoch ms */
  lastSeenAt: epochMs,
  /** 1초 스냅샷을 보내는 인스턴스인가. 거짓이면 트레이스만 있는 것이다 */
  realtime: z.boolean(),
  /** 조용해졌다. 아직 목록에는 있지만 지금 상태를 말할 수 없다 */
  stale: z.boolean(),
  /** 이 이름으로 다른 프로세스가 뜬 것을 본 시각 (재시작·재배포) */
  restartedAt: epochMs.optional(),
  /** 대상 프로세스가 뜬 시각. 에이전트가 스냅샷에 싣는 값이다 */
  processStartedAt: epochMs.optional(),
  /**
   * 같은 `service`·`instance` 이름으로 **여러 프로세스**가 번갈아 보고하고 있다.
   * 인스턴스 이름을 손으로 고정해 두고 컨테이너를 둘 띄웠거나, `NODE_OPTIONS`가 자식에게
   * 상속돼 부모·워커 프로세스까지 함께 붙은 경우다(Next dev는 다섯이다). 그 이름의
   * 게이지·알림은 여러 프로세스 값이 섞여 있으므로 믿을 수 없다
   */
  conflict: z.boolean().default(false),
});
export type InstancePresence = z.infer<typeof InstancePresence>;
